import { useState, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"

import SavedProjectsStacksItem from "./SavedProjectsStacksItem"
import { useAccountId, useProjectList } from "../../../hooks"
import { useProjectsStore } from "../../../stores/useProjectsStore"
import { SAVED_STACKS } from "./constants"
import { getProjectsData } from "../../../shared/utils"
import { SavedAccountProjectsTypes } from "@/types/savedAccountProjects"
import { SavedStackType } from "../types"
import { ProgramType, ProjectListTypes } from "@/types/program"

export interface SavedProjectsStacksProps {
  savedAccountProjects?: SavedAccountProjectsTypes
}

const SavedProjectsStacks = ({
  savedAccountProjects,
}: SavedProjectsStacksProps) => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()
  const { category } = useProjectsStore()

  const [active, setActive] = useState<SavedStackType | null>(null)
  const [expanded, setExpanded] = useState<SavedStackType | null>(null)

  const { data: projectsList, isLoading: projectListIsLoading } =
    useProjectList<ProjectListTypes, Error>(queryClient, accountId, {})

  const programsData: ProgramType[] = useMemo(
    () => getProjectsData(projectsList),
    [projectsList]
  )

  const handleToggle = (key: SavedStackType | null) => {
    if (expanded && expanded !== key) {
      setExpanded(null)
      setActive(key)

      setTimeout(() => setExpanded(key), 850)
    } else {
      setActive(expanded === key ? null : key)
      setExpanded(expanded === key ? null : key)
    }
  }

  const shouldRender =
    category === "saved_programs" &&
    savedAccountProjects?.saved_account_wizards &&
    Object.keys(savedAccountProjects.saved_account_wizards).length > 0

  if (!shouldRender) return null

  return (
    <>
      {Object.entries(savedAccountProjects.saved_account_wizards).map(
        ([key, savedPrograms]: [string, SavedAccountProjectsTypes]) => {
          const stackKey = key as SavedStackType
          const projectIds = new Set(
            savedPrograms?.account_projects?.map((p) => p.project_id)
          )
          const matchingPrograms = programsData.filter((p) =>
            projectIds.has(p.id)
          )

          return (
            <SavedProjectsStacksItem
              key={stackKey}
              stackKey={stackKey}
              title={SAVED_STACKS[stackKey].title}
              image={SAVED_STACKS[stackKey].image}
              timestamp={savedPrograms.timestamp}
              programs={matchingPrograms}
              isLoading={projectListIsLoading}
              active={active}
              expanded={expanded}
              onToggle={handleToggle}
            />
          )
        }
      )}
    </>
  )
}

export default SavedProjectsStacks
